/**
 * Условия работ на объекте — МСК Газобетон.
 *
 * Технику для разгрузки даёт перевозчик, а зацепить и отцепить поддоны должны
 * стропальщики, и их даёт заказчик: в стоимость разгрузки их работа не входит.
 * Это верно и для манипулятора, и для крана.
 * Менеджер узнаёт об этом из карточки машины, заказчик — из готового сообщения.
 * Оба текста живут здесь, но слова у них разные намеренно: карточка говорит о
 * заказчике в третьем лице, а сообщение обращается к нему напрямую («с Вашей
 * стороны»). Общий тут смысл, а не буквы.
 *
 * Своих расчётов и своего состояния здесь нет: показывать или нет, решает
 * выбранный способ разгрузки из готового расчёта — а в карточке машины ещё и
 * сам факт манипулятора с грузом: своё он снимает и без оплаченной разгрузки
 * (правило I спеки), и стропальщики там нужны ровно так же.
 */

import { isGazUnloadingOrderedMethod } from '../Information/gazUnloadMethod.js';

/** Заголовок блока в карточке машины. */
export const GAZ_SLINGER_TITLE = 'Стропальщиков предоставляет заказчик';

/** Пояснение под заголовком — там же и в сообщении заказчику. */
export const GAZ_SLINGER_NOTE = 'Их услуги в стоимость разгрузки не входят';

/**
 * Где именно снимают груз. Манипулятор работает стрелой от машины, а не носит
 * поддоны по участку: разгрузка идёт рядом с бортом, дальше груз перемещает
 * заказчик. Стоит там же, где условие о стропальщиках, — это условие той же работы.
 */
export const GAZ_UNLOADING_SIDE_NOTE = 'Разгрузка — рядом с бортом машины';

/**
 * Подъезд к объекту. Ни шаланда, ни манипулятор не везде проходят, и заранее это
 * известно не всегда: если машина на объект не проезжает, цену доставки заново
 * согласовывает логист. Условие доставки, а не разгрузки, поэтому показывается
 * всегда внутри области, даже когда разгрузка не заказана.
 */
export const GAZ_ACCESS_NOTE = 'Если машина не проезжает на объект, стоимость доставки согласовывается с логистом';

// Выбранный способ разгрузки из расчёта. Источник истины один: не тип машины и
// не название транспорта.
const unloadMethodOf = (breakdown) => String(breakdown?.unloadMethod || '').trim().toLowerCase();

/**
 * Нужны ли стропальщики.
 *
 * Нужны при любой разгрузке: и манипулятором, и краном — цеплять и отцеплять
 * поддоны приходится в обоих случаях, и в обоих это работа заказчика.
 *
 * И нужны там, где оплаченной разгрузки нет вовсе, но в заказе едет манипулятор:
 * свой груз он снимает сам (правило I спеки), значит поддоны на объекте всё равно
 * кто-то цепляет, и это по-прежнему заказчик. Признак манипулятора берётся из
 * расчёта, а не из названия транспорта.
 *
 * Не нужны, когда весь заказ везут шаланды и разгрузка не заказана. Пустой или
 * неизвестный способ разгрузки сам по себе тоже ничего не показывает.
 */
export const isGazUnloadingWithSlingers = (breakdown) => {
  const method = unloadMethodOf(breakdown);
  return method.includes('манипулятор')
    || method.includes('кран')
    || breakdown?.manipulatorGoesWithShalands === true;
};

/**
 * То же условие для сообщения заказчику — двумя строками и обращением к нему.
 *
 * Текст согласован дословно: заказчик читает, что стропальщики нужны с его
 * стороны и что снимают груз у борта. Точек в конце нет — так и утверждали.
 *
 * Условие пишется только под заказанную разгрузку — манипулятором или краном.
 * При «без разгрузки» манипулятор в КП назван просто манипулятором и разгрузку
 * заказчику не обещает, поэтому условия манипуляторной разгрузки в текст не
 * идут: иначе КП обещает работу, которой в нём нет. Менеджеру в карточке машины
 * условие остаётся — там оно про правило I, а не про оплаченную услугу.
 *
 * @returns {string|null} null, когда разгрузка не заказана
 */
export const buildGazSlingerNoticeText = (breakdown) => (
  isGazUnloadingWithSlingers(breakdown) && isGazUnloadingOrderedMethod(breakdown?.unloadMethod)
    ? 'Важно: на разгрузку должны быть стропальщики с Вашей стороны'
      + '\nРазгрузка рядом с бортом'
    : null
);
